import { useEffect, useState, type FormEvent } from 'react';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge } from '../components/ui/Badge';
import type { BadgeTon } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Input } from '../components/ui/Input';
import { IconAbrechnungen, IconHinweis, IconPlus } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { DEMO_ABRECHNUNGEN, DEMO_OBJEKTE, STATUS_BEZEICHNUNG } from '../lib/demodaten';
import { formatDatum, formatZeitraum } from '../lib/format';

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

const STATUS_TON: Record<string, BadgeTon> = {
  offen: 'neutral',
  berechnet: 'info',
  festgeschrieben: 'info',
  versendet: 'erfolg',
  storniert: 'warnung',
  entwurf: 'neutral',
};
const STATUS_TEXT: Record<string, string> = {
  offen: 'Offen',
  berechnet: 'Berechnet',
  festgeschrieben: 'Festgeschrieben',
  versendet: 'Versendet',
  storniert: 'Storniert',
};

interface ObjektRef {
  id: string;
  bezeichnung: string;
}
interface AbrAnsicht {
  id: string;
  objektBezeichnung: string;
  zeitraumVon: string;
  zeitraumBis: string;
  status: string;
  fristBis: string | null;
  tageBisFrist: number | null;
  ampel: string | null;
  nummer: string | null;
}

interface ApiAbr {
  id: string;
  objektBezeichnung?: string;
  zeitraumVon: string;
  zeitraumBis: string;
  status: string;
  fristBis: string;
  tageBisFrist?: number;
  ampel?: string;
  nummer?: string | null;
}

function ausApi(a: ApiAbr): AbrAnsicht {
  return {
    id: a.id,
    objektBezeichnung: a.objektBezeichnung ?? 'Objekt',
    zeitraumVon: a.zeitraumVon,
    zeitraumBis: a.zeitraumBis,
    status: a.status,
    fristBis: a.fristBis,
    tageBisFrist: a.tageBisFrist ?? null,
    ampel: a.ampel ?? null,
    nummer: a.nummer ?? null,
  };
}

function fristBisDemo(bis: string): string {
  const [y, m, d] = bis.split('-').map(Number);
  return new Date(Date.UTC(y + 1, m - 1, d)).toISOString().slice(0, 10);
}

function demoAbrechnungen(): AbrAnsicht[] {
  return DEMO_ABRECHNUNGEN.map((a) => ({
    id: a.id,
    objektBezeichnung: a.objekt,
    zeitraumVon: a.von,
    zeitraumBis: a.bis,
    status: a.status,
    fristBis: fristBisDemo(a.bis),
    tageBisFrist: null,
    ampel: a.status === 'entwurf' ? 'gelb' : null,
    nummer: a.status === 'entwurf' ? null : `ABR-000${a.id.slice(-1)}`,
  }));
}

export interface AbrechnungenProps {
  onNeueAbrechnung?: () => void;
}

export function Abrechnungen(_props: AbrechnungenProps) {
  const [objekte, setObjekte] = useState<ObjektRef[]>(
    DEMO ? DEMO_OBJEKTE.map((o) => ({ id: o.id, bezeichnung: o.bezeichnung })) : [],
  );
  const [abr, setAbr] = useState<AbrAnsicht[] | null>(DEMO ? demoAbrechnungen() : null);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function laden_() {
    if (DEMO) return;
    setLaden(true);
    try {
      const [a, o] = await Promise.all([
        api.get<{ abrechnungen: ApiAbr[] }>('/abrechnungen'),
        api.get<{ objekte: ObjektRef[] }>('/objekte'),
      ]);
      setAbr(a.abrechnungen.map(ausApi));
      setObjekte(o.objekte);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Abrechnungen konnten nicht geladen werden.');
    } finally {
      setLaden(false);
    }
  }

  useEffect(() => {
    void laden_();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Seitenkopf
        titel="Abrechnungen"
        beschreibung="Abrechnungsläufe je Objekt und Zeitraum. Die Ausschlussfrist endet zwölf Monate nach Ende des Abrechnungszeitraums (§ 556 Abs. 3 BGB)."
        aktion={
          <Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen((o) => !o)} disabled={objekte.length === 0}>
            Neue Abrechnung
          </Button>
        }
      />

      {meldung && (
        <p className="mb-4 rounded-lg bg-success-soft px-3 py-2 text-sm text-success-soft-fg">{meldung}</p>
      )}

      {formOffen && (
        <AbrechnungFormular
          objekte={objekte}
          onFertig={(m) => {
            setMeldung(m);
            setFormOffen(false);
            void laden_();
          }}
          onNeuLokal={(a) => setAbr((xs) => [a, ...(xs ?? [])])}
          onAbbruch={() => setFormOffen(false)}
        />
      )}

      {laden && !abr ? (
        <p className="text-sm text-fg-muted">Abrechnungen werden geladen …</p>
      ) : fehler && !abr ? (
        <Card>
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      ) : abr && abr.length === 0 ? (
        <EmptyState
          icon={<IconAbrechnungen groesse={22} />}
          titel="Noch keine Abrechnung"
          beschreibung="Starten Sie einen Abrechnungslauf für ein Objekt und einen Zeitraum. Die Belege, Zähler und Heizkosten werden automatisch verrechnet."
          aktion={<Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen(true)}>Neue Abrechnung</Button>}
        />
      ) : (
        <div className="space-y-3">
          {(abr ?? []).map((a) => (
            <Card key={a.id}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-fg">{a.objektBezeichnung}</h2>
                    <Badge ton={STATUS_TON[a.status] ?? 'neutral'} punkt>
                      {STATUS_TEXT[a.status] ?? STATUS_BEZEICHNUNG[a.status as keyof typeof STATUS_BEZEICHNUNG] ?? a.status}
                    </Badge>
                    {a.nummer && <Badge ton="neutral">Nr. {a.nummer}</Badge>}
                  </div>
                  <p className="mt-1 text-sm text-fg-muted">
                    Abrechnungszeitraum {formatZeitraum(a.zeitraumVon, a.zeitraumBis)}
                  </p>
                  {a.fristBis && (
                    <p className="mt-1 text-sm text-fg-muted">Abrechnungsfrist bis {formatDatum(a.fristBis)}</p>
                  )}
                </div>
                {a.ampel === 'rot' || a.ampel === 'gelb' ? (
                  <Badge ton={a.ampel === 'rot' ? 'gefahr' : 'warnung'} punkt>
                    {a.tageBisFrist !== null && a.tageBisFrist < 0
                      ? 'Frist verstrichen'
                      : a.tageBisFrist !== null
                        ? `noch ${a.tageBisFrist} Tage`
                        : 'Frist beachten'}
                  </Badge>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}

      <p className="mt-6 flex items-start gap-2 rounded-lg bg-accent-soft px-3 py-2 text-sm text-accent-soft-fg">
        <span className="mt-0.5 shrink-0">
          <IconHinweis groesse={16} />
        </span>
        Nach dem Festschreiben ist eine Abrechnung unveränderlich (GoBD). Eine Korrektur erfolgt über
        eine Korrekturabrechnung, nicht durch Bearbeiten.
      </p>
    </div>
  );
}

function AbrechnungFormular({
  objekte,
  onFertig,
  onNeuLokal,
  onAbbruch,
}: {
  objekte: ObjektRef[];
  onFertig: (meldung: string) => void;
  onNeuLokal: (a: AbrAnsicht) => void;
  onAbbruch: () => void;
}) {
  const jahr = new Date().getFullYear() - 1;
  const [objektId, setObjektId] = useState(objekte[0]?.id ?? '');
  const [von, setVon] = useState(`${jahr}-01-01`);
  const [bis, setBis] = useState(`${jahr}-12-31`);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!objektId || bis < von) {
      setFehler('Bitte ein Objekt wählen; der Zeitraum darf nicht rückwärts laufen.');
      return;
    }
    setSendet(true);
    try {
      if (DEMO) {
        const name = objekte.find((o) => o.id === objektId)?.bezeichnung ?? 'Objekt';
        onNeuLokal({
          id: `neu-${Date.now()}`,
          objektBezeichnung: name,
          zeitraumVon: von,
          zeitraumBis: bis,
          status: 'berechnet',
          fristBis: fristBisDemo(bis),
          tageBisFrist: null,
          ampel: null,
          nummer: null,
        });
        onFertig('Abrechnung angelegt und berechnet (Vorschau).');
        return;
      }
      const angelegt = await api.post<{ abrechnung: { id: string } }>('/abrechnungen', {
        objektId,
        zeitraumVon: von,
        zeitraumBis: bis,
      });
      let meldung = 'Abrechnung angelegt.';
      try {
        await api.post(`/abrechnungen/${angelegt.abrechnung.id}/berechnen`);
        meldung = 'Abrechnung angelegt und durchgerechnet.';
      } catch (berechnenFehler) {
        meldung =
          'Abrechnung angelegt. Der Durchlauf meldete: ' +
          (berechnenFehler instanceof ApiFehler ? berechnenFehler.message : 'Bitte Belege und Zähler prüfen.');
      }
      onFertig(meldung);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Abrechnung konnte nicht angelegt werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Neue Abrechnung" className="mb-4">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="block sm:col-span-1">
            <span className="mb-1.5 block text-sm font-medium text-fg">Objekt</span>
            <select className={feldKlasse} value={objektId} onChange={(e) => setObjektId(e.target.value)}>
              {objekte.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.bezeichnung}
                </option>
              ))}
            </select>
          </label>
          <Input label="Zeitraum von" type="date" value={von} onChange={(e) => setVon(e.target.value)} />
          <Input label="Zeitraum bis" type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
        </div>

        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird berechnet …' : 'Abrechnung starten'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default Abrechnungen;
